import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Container, Heading, Text, VStack, SimpleGrid, Flex, Icon, List, ListItem, ListIcon, Button } from "@chakra-ui/react";
import { Briefcase, Plane, TreePalm, Check, ArrowRight, ExternalLink } from "lucide-react";
import { useLocalPath, useText } from "../../i18n/lang";

const TEXT = {
  en: {
    title: "Who we work for",
    intro: "One team, three ways to travel with Travel Time.",
    cards: [
      {
        label: "Companies",
        title: "Business travel, events and consultancy",
        points: [
          ["Corporate travel management with 24/7 support", "/servicii#corporate-travel"],
          ["Corporate events and MICE, end to end", "/servicii#events-management"],
          ["Travel program consultancy and cost optimization", "/capabilitati"],
        ],
        cta: "See our services",
      },
      {
        label: "Travel agencies",
        title: "Booking Time, the B2B platform for agencies",
        points: [
          ["200+ airlines on Amadeus and direct NDC fares", null],
          ["300+ low-cost carriers and 800,000+ hotels", null],
          ["Service fees from €3 per ticket", null],
        ],
        cta: "Discover Booking Time",
      },
      {
        label: "Holidays",
        title: "Holidays booked online",
        points: [
          ["Package holidays by plane or coach", null],
          ["Tours and hotel stays", null],
          ["Online booking on vacante.travel-time.ro", null],
        ],
        cta: "See holiday offers",
      },
    ],
  },
  ro: {
    title: "Pentru cine lucrăm",
    intro: "O singură echipă, trei moduri de a călători cu Travel Time.",
    cards: [
      {
        label: "Companii",
        title: "Călătorii de afaceri, evenimente și consultanță",
        points: [
          ["Managementul călătoriilor de afaceri, cu suport 24/7", "/servicii#corporate-travel"],
          ["Evenimente corporate și MICE, de la A la Z", "/servicii#events-management"],
          ["Consultanță pentru programe de travel și optimizarea costurilor", "/capabilitati"],
        ],
        cta: "Vezi serviciile",
      },
      {
        label: "Agenții de turism",
        title: "Booking Time, platforma B2B pentru agenții",
        points: [
          ["Peste 200 de companii aeriene în Amadeus și tarife NDC directe", null],
          ["Peste 300 de companii low-cost și 800.000 de hoteluri", null],
          ["Comisioane de la 3 € pe bilet", null],
        ],
        cta: "Descoperă Booking Time",
      },
      {
        label: "Vacanțe",
        title: "Vacanțe rezervate online",
        points: [
          ["Pachete de vacanță cu avionul sau cu autocarul", null],
          ["Circuite și cazare la hoteluri", null],
          ["Rezervare online pe vacante.travel-time.ro", null],
        ],
        cta: "Vezi ofertele de vacanță",
      },
    ],
  },
};

const STYLE = [
  { icon: Briefcase, color: "blue", to: "/servicii" },
  { icon: Plane, color: "orange", to: "/agentii-partenere" },
  { icon: TreePalm, color: "teal", href: "https://vacante.travel-time.ro/?utm_source=travel-time.ro&utm_medium=home" },
];

const HomeAudiences = () => {
  const t = useText(TEXT);
  const localPath = useLocalPath();
  const toTop = () => window.scrollTo(0, 0);

  return (
    <Box py={{ base: 16, md: 24 }}>
      <Container maxW="container.xl">
        <VStack spacing={4} textAlign="center" mb={{ base: 10, md: 14 }}>
          <Heading as="h2" fontFamily="Montserrat" fontWeight="700" fontSize={{ base: "2xl", md: "4xl" }} color="gray.800">
            {t.title}
          </Heading>
          <Text fontSize={{ base: "md", md: "lg" }} color="gray.600">
            {t.intro}
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6}>
          {t.cards.map((card, index) => {
            const style = STYLE[index];
            const linkProps = style.href
              ? { as: "a", href: style.href, target: "_blank", rel: "noopener" }
              : { as: RouterLink, to: localPath(style.to), onClick: toTop };
            return (
              <Flex
                key={card.label}
                direction="column"
                bg="white"
                borderRadius="2xl"
                borderWidth="1px"
                borderColor="gray.100"
                borderTopWidth="4px"
                borderTopColor={`${style.color}.400`}
                boxShadow="0 14px 40px -22px rgba(15, 40, 80, 0.45)"
                p={{ base: 6, md: 8 }}
                transition="all 0.2s"
                _hover={{ transform: "translateY(-4px)", boxShadow: "0 22px 48px -22px rgba(15, 40, 80, 0.5)" }}
              >
                <Flex w="52px" h="52px" borderRadius="xl" bg={`${style.color}.50`} align="center" justify="center" mb={5}>
                  <Icon as={style.icon} boxSize={6} color={`${style.color}.500`} />
                </Flex>
                <Text fontSize="sm" fontWeight="700" textTransform="uppercase" letterSpacing="wider" color={`${style.color}.600`} mb={1}>
                  {card.label}
                </Text>
                <Heading as="h3" fontFamily="Montserrat" fontWeight="700" fontSize={{ base: "xl", md: "2xl" }} color="gray.800" mb={5} lineHeight="1.3">
                  {card.title}
                </Heading>
                <List spacing={3} mb={8} flex="1">
                  {card.points.map(([text, path]) => (
                    <ListItem key={text} display="flex" alignItems="flex-start" color="gray.700" fontSize="md" lineHeight="1.5">
                      <ListIcon as={Check} color={`${style.color}.500`} mt={1} />
                      {path ? (
                        <Box as={RouterLink} to={localPath(path.split("#")[0]) + (path.includes("#") ? `#${path.split("#")[1]}` : "")} _hover={{ color: `${style.color}.600`, textDecoration: "underline" }}>
                          {text}
                        </Box>
                      ) : (
                        <span>{text}</span>
                      )}
                    </ListItem>
                  ))}
                </List>
                <Button
                  {...linkProps}
                  alignSelf="flex-start"
                  variant="outline"
                  colorScheme={style.color}
                  rightIcon={<Icon as={style.href ? ExternalLink : ArrowRight} boxSize={4} />}
                >
                  {card.cta}
                </Button>
              </Flex>
            );
          })}
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default HomeAudiences;
